// src/pages/CheckoutPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import shopApi, { formatPrice, detectCountry } from '../services/shopApi';
import { useAuth } from '../hooks/useAuth';
import './CheckoutPage.css';

export default function CheckoutPage({ cart, totals, onOrderSuccess }) {
  const navigate = useNavigate();
  const { consumer, isLoggedIn, openLogin } = useAuth();
  const { subtotal, shipping, tax, total } = totals;

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    street: '', city: '', state: '', postalCode: '', country: 'United States',
    paymentMethod: 'cod',
    note: '',
  });
  const [errors,       setErrors]       = useState({});
  const [submitting,   setSubmitting]   = useState(false);
  const [stripeReady,  setStripeReady]  = useState(false);
  const [stripeInst,   setStripeInst]   = useState(null);
  const [stripeEls,    setStripeEls]    = useState(null);
  const [stripeError,  setStripeError]  = useState('');
  const [submitError,  setSubmitError]  = useState('');

  // ── FIX: Read currency synchronously from localStorage ──────────
  // useEffect fires AFTER first render — currency was 'USD' on first paint
  // useState(initFn) runs synchronously so ₫ shows immediately on load
  const getLocalCurrency = () => {
    try {
      const geo = JSON.parse(localStorage.getItem('jt_country') || '{}');
      return geo.currency || 'USD';
    } catch { return 'USD'; }
  };

  const [detectedCurrency, setDetectedCurrency] = useState(getLocalCurrency);

  useEffect(() => {
    if (totals?.currency) {
      setDetectedCurrency(totals.currency);
      return;
    }
    if (cart.items?.[0]?.currency) {
      setDetectedCurrency(cart.items[0].currency);
      return;
    }
    detectCountry().then(geo => {
      if (geo?.currency) setDetectedCurrency(geo.currency);
    });
  }, [totals, cart.items]);

  // Resolved currency: backend → item → localStorage/geo-detected
  const currency = totals?.currency || cart.items?.[0]?.currency || detectedCurrency;
  const fmt = (amount) => formatPrice(amount, currency);
  // ────────────────────────────────────────────────────────────────

  // Auto-fill form when consumer logs in
  useEffect(() => {
    if (consumer) {
      setForm(f => ({
        ...f,
        firstName:  consumer.firstName || f.firstName,
        lastName:   consumer.lastName  || f.lastName,
        email:      consumer.email     || f.email,
        phone:      consumer.phone     || f.phone,
        street:     consumer.defaultAddress?.street     || f.street,
        city:       consumer.defaultAddress?.city       || f.city,
        state:      consumer.defaultAddress?.state      || f.state,
        postalCode: consumer.defaultAddress?.postalCode || f.postalCode,
        country:    consumer.defaultAddress?.country    || f.country,
      }));
    }
  }, [consumer]);

  // Load Stripe when user selects credit card
  useEffect(() => {
    if (form.paymentMethod !== 'stripe') return;
    if (stripeInst) return;
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) return;
    const check = setInterval(() => {
      if (window.Stripe) {
        clearInterval(check);
        setStripeInst(window.Stripe(key));
        setStripeReady(true);
      }
    }, 100);
    return () => clearInterval(check);
  }, [form.paymentMethod, stripeInst]);

  if (!cart.items?.length) {
    return (
      <div className="page-wrap" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p>Your cart is empty.</p>
        <Link to="/products" className="btn-primary" style={{ marginTop: 16 }}>Shop Now</Link>
      </div>
    );
  }

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.street.trim())  e.street  = 'Required';
    if (!form.city.trim())    e.city    = 'Required';
    if (!form.country.trim()) e.country = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError('');
    setStripeError('');
    try {
      const r = await shopApi.createOrder({
        customer:        { firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone },
        shippingAddress: { street: form.street, city: form.city, state: form.state, postalCode: form.postalCode, country: form.country },
        payment:         { method: form.paymentMethod },
        customerNote:    form.note,
      });

      if (!r.success) {
        console.error('[order] 400 error:', r);
        setSubmitError(r.message || 'Order failed.');
        return;
      }

      const orderId = r.data?.orderId || r.data?.order?.orderId;

      if (form.paymentMethod === 'stripe') {
        const { error: submitErr } = await stripeEls.submit();
        if (submitErr) { setStripeError(submitErr.message || 'Please check your card details.'); return; }

        const intentRes = await shopApi.payment.stripeCreateIntent({ orderId });
        if (!intentRes.success || !intentRes.clientSecret) {
          setSubmitError(intentRes.message || 'Could not initialize payment.');
          return;
        }

        const { error, paymentIntent } = await stripeInst.confirmPayment({
          elements:     stripeEls,
          clientSecret: intentRes.clientSecret,
          confirmParams: {
            return_url: `${window.location.origin}/order-success?orderId=${orderId}`,
            payment_method_data: {
              billing_details: {
                name:  `${form.firstName} ${form.lastName}`,
                email: form.email,
              },
            },
          },
          redirect: 'if_required',
        });
        if (error) { setStripeError(error.message || 'Payment failed.'); return; }
        if (paymentIntent?.status === 'succeeded') {
          await shopApi.payment.stripeConfirm({ orderId, paymentIntentId: paymentIntent.id });
          onOrderSuccess?.({ orderId });
          navigate(`/order-success?orderId=${orderId}`);
        }
      } else {
        onOrderSuccess?.(r.data?.order || { orderId });
        navigate(`/order-success?orderId=${orderId}`);
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="checkout-page page-wrap">
      <div className="checkout-layout">

        {/* ── Form column ─────────────────────────────────────────── */}
        <div className="checkout-form-col">
          <Link to="/cart" className="back-link">← Back to cart</Link>
          <h1 className="checkout-title">Checkout</h1>

          {/* Login prompt for guests */}
          {!isLoggedIn && (
            <div style={{
              background: 'rgba(0,229,204,0.06)',
              border: '1px solid rgba(0,229,204,0.15)',
              borderRadius: 12, padding: '12px 16px',
              marginBottom: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12,
            }}>
              <div style={{ fontSize: 13, color: 'rgba(224,240,240,0.6)' }}>
                <span style={{ color: '#00E5CC', fontWeight: 600 }}>Have an account?</span>
                {' '}Sign in to auto-fill your details and earn loyalty points.
              </div>
              <button onClick={openLogin} style={{
                background: 'rgba(0,229,204,0.12)',
                border: '1px solid rgba(0,229,204,0.3)',
                color: '#00E5CC', borderRadius: 8,
                padding: '6px 14px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                Sign In
              </button>
            </div>
          )}

          {/* Logged in badge */}
          {isLoggedIn && (
            <div style={{
              background: 'rgba(34,217,127,0.06)',
              border: '1px solid rgba(34,217,127,0.15)',
              borderRadius: 12, padding: '10px 16px',
              marginBottom: 24, fontSize: 13,
              color: 'rgba(224,240,240,0.6)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>✓</span>
              <span>
                Signed in as <strong style={{ color: '#22D97F' }}>{consumer?.firstName} {consumer?.lastName}</strong>
                {' '}— details pre-filled
              </span>
            </div>
          )}

          {/* Customer */}
          <div className="form-block">
            <div className="form-block-title">Customer Information</div>
            <div className="form-row-2">
              <Field label="First Name" required error={errors.firstName}>
                <input value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="John" />
              </Field>
              <Field label="Last Name" required error={errors.lastName}>
                <input value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Doe" />
              </Field>
            </div>
            <div className="form-row-2">
              <Field label="Email" required error={errors.email}>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="john@email.com" />
              </Field>
              <Field label="Phone">
                <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 555 000 0000" />
              </Field>
            </div>
          </div>

          {/* Address */}
          <div className="form-block">
            <div className="form-block-title">Shipping Address</div>
            <Field label="Street Address" required error={errors.street}>
              <input value={form.street} onChange={e => set('street', e.target.value)} placeholder="123 Main Street" />
            </Field>
            <div className="form-row-2">
              <Field label="City" required error={errors.city}>
                <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="New York" />
              </Field>
              <Field label="State / Province">
                <input value={form.state} onChange={e => set('state', e.target.value)} placeholder="NY" />
              </Field>
            </div>
            <div className="form-row-2">
              <Field label="Postal Code">
                <input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} placeholder="10001" />
              </Field>
              <Field label="Country" required error={errors.country}>
                <select value={form.country} onChange={e => set('country', e.target.value)}>
                  {['United States','Canada','Australia','United Kingdom','Vietnam','Singapore','Japan','Other'].map(c => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          {/* Payment */}
          <div className="form-block">
            <div className="form-block-title">Payment Method</div>
            <div className="payment-options">
              {[
                { value: 'stripe',       icon: '💳', label: 'Credit Card',      desc: 'Visa, Mastercard, Amex' },
                { value: 'cod',          icon: '💵', label: 'Cash on Delivery', desc: 'Pay when your order arrives' },
                { value: 'bank_transfer',icon: '🏦', label: 'Bank Transfer',    desc: 'Transfer to our bank account' },
              ].map(opt => (
                <label key={opt.value} className={`payment-opt ${form.paymentMethod === opt.value ? 'active' : ''}`}>
                  <input type="radio" name="payment" value={opt.value}
                    checked={form.paymentMethod === opt.value}
                    onChange={() => set('paymentMethod', opt.value)} />
                  <span className="opt-icon">{opt.icon}</span>
                  <div>
                    <div className="opt-label">{opt.label}</div>
                    <div className="opt-desc">{opt.desc}</div>
                  </div>
                </label>
              ))}
              {form.paymentMethod === 'stripe' && (
                <div style={{ marginTop: 16 }}>
                  {!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? (
                    <div style={{ padding:'12px', background:'rgba(239,68,68,0.1)', borderRadius:8, color:'#f87171', fontSize:13 }}>
                      ⚠️ Add REACT_APP_STRIPE_PUBLISHABLE_KEY to frontend .env
                    </div>
                  ) : stripeReady && stripeInst ? (
                    // ✅ FIX: use resolved currency instead of hardcoded 'USD'
                    <StripeForm stripe={stripeInst} amount={total} currency={currency} onReady={setStripeEls} error={stripeError} />
                  ) : (
                    <div style={{ padding:12, color:'var(--text-muted)', fontSize:13 }}>Loading payment form…</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Note */}
          <div className="form-block">
            <div className="form-block-title">Order Notes (Optional)</div>
            <textarea
              value={form.note}
              onChange={e => set('note', e.target.value)}
              placeholder="Any special instructions..."
              rows={3}
              className="note-area"
            />
          </div>

          {submitError && (
            <div className="submit-error">{submitError}</div>
          )}

          {/* ✅ FIX: use fmt() instead of formatPrice(total) */}
          <button
            className="btn-primary place-order-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Placing Order...' : `Place Order — ${fmt(total)}`}
          </button>
        </div>

        {/* ── Order Summary column ─────────────────────────────────── */}
        <div className="checkout-summary-col">
          <div className="checkout-summary-card">
            <div className="summary-title">Order Summary</div>

            {cart.items.map(item => {
              // Use item's own currency if available, otherwise resolved currency
              const itemCurrency = item.currency || currency;
              return (
                <div key={item._id} className="co-item">
                  <div className="co-item-img">
                    {item.image ? <img src={item.image} alt={item.name} /> : '📦'}
                  </div>
                  <div className="co-item-info">
                    <div className="co-item-name">{item.name}</div>
                    {item.variant?.label && <div className="co-item-variant">{item.variant.label}</div>}
                    <div className="co-item-qty">Qty: {item.quantity}</div>
                  </div>
                  {/* ✅ FIX: pass itemCurrency */}
                  <div className="co-item-price">
                    {formatPrice(item.price * item.quantity, itemCurrency)}
                  </div>
                </div>
              );
            })}

            <div className="co-divider" />
            {/* ✅ FIX: all summary rows now use fmt() with detected currency */}
            <div className="co-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="co-row">
              <span>Shipping</span>
              <span className={shipping === 0 ? 'free' : ''}>
                {shipping === 0 ? 'Free' : fmt(shipping)}
              </span>
            </div>
            <div className="co-row"><span>Tax (10%)</span><span>{fmt(tax)}</span></div>
            <div className="co-divider" />
            <div className="co-row total"><span>Total</span><span>{fmt(total)}</span></div>

            {isLoggedIn && (
              <div style={{
                marginTop: 12, padding: '10px 12px',
                background: 'rgba(0,229,204,0.06)',
                border: '1px solid rgba(0,229,204,0.12)',
                borderRadius: 8, fontSize: 12,
                color: 'rgba(224,240,240,0.5)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span>🏅</span>
                <span>You'll earn <strong style={{ color: '#00E5CC' }}>{Math.floor(total / 1000)} points</strong> for this order</span>
              </div>
            )}
          </div>

          <div className="co-trust">
            <div className="co-trust-item">🔒 Secure checkout</div>
            <div className="co-trust-item">✓ Verified products</div>
            <div className="co-trust-item">↩️ 30-day returns</div>
          </div>
        </div>

      </div>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div className="form-field">
      <label>{label}{required && <span className="req">*</span>}</label>
      {children}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

// ── HOW TO VERIFY AUTHENTICITY ──────────────────────────────
export function AuthenticityGuide() {
  const steps = [
    { icon: '📦', step: '1', title: 'Receive Your Product',  desc: 'Your order arrives with a JepTags seal on the packaging.' },
    { icon: '📱', step: '2', title: 'Open JepTags App',      desc: 'Download the free JepTags app on iOS or Android.' },
    { icon: '🏷️', step: '3', title: 'Scan the Tag',         desc: 'Tap the NFC tag or scan the QR code on your product.' },
    { icon: '✅', step: '4', title: 'Instantly Verified',    desc: 'See a green "Authentic" result in under a second.' },
  ];
  return (
    <div className="auth-guide-section">
      <div className="auth-guide-inner">
        <div className="auth-guide-header">
          <div className="badge badge-cyan" style={{ marginBottom: 12 }}>Every Product Protected</div>
          <h2>How to Verify <span className="grad">Authenticity</span></h2>
          <p>After your order arrives, use the JepTags app to confirm your product is 100% genuine — it takes less than 5 seconds.</p>
        </div>
        <div className="auth-steps">
          {steps.map(s => (
            <div key={s.step} className="auth-step">
              <div className="auth-step-num">{s.step}</div>
              <div className="auth-step-icon">{s.icon}</div>
              <div className="auth-step-title">{s.title}</div>
              <div className="auth-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
        <div className="auth-result-demo">
          <div className="auth-result auth-ok">
            <div className="auth-dot green" />
            <div>
              <div className="auth-result-label">✅ Authentic</div>
              <div className="auth-result-sub">Product verified — it's the real thing</div>
            </div>
          </div>
          <div className="auth-result auth-fail">
            <div className="auth-dot red" />
            <div>
              <div className="auth-result-label">⚠️ Not Verified</div>
              <div className="auth-result-sub">Do not accept — contact support immediately</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StripeForm({ stripe, amount, currency, onReady, error }) {
  const ref     = React.useRef(null);
  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (!stripe || !ref.current || mounted.current) return;
    mounted.current = true;
    const els = stripe.elements({
      mode:     'payment',
      amount:   Math.round(amount * 100),
      currency: (currency || 'USD').toLowerCase(),
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary:    '#00E5CC',
          colorBackground: '#0a1628',
          colorText:       '#E0F0F0',
          borderRadius:    '8px',
        },
      },
    });
    const el = els.create('payment', { layout: 'tabs' });
    el.mount(ref.current);
    onReady(els);
    return () => { el.unmount(); mounted.current = false; };
  }, [stripe]);

  return (
    <div>
      <div ref={ref} style={{ minHeight: 120 }} />
      {error && (
        <div style={{
          marginTop: 10, padding: '10px 14px',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, color: '#f87171', fontSize: 13,
        }}>
          ⚠️ {error}
        </div>
      )}
    </div>
  );
}